import "./print-modal-v2.css";
import { useContext, useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Box,
  ModalCloseButton,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  TableContainer,
  Table,
  Tr,
  Th,
  Thead,
  Tbody,
  Td,
  Flex,
  useDisclosure,
  Spinner,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useParams } from "react-router-dom";
import MonthSelect from "components/elements/StatementCreation/MonthlySelect";
import { SelectOption } from "utils/types";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import InterestStatamentPopupContent from "./interest-statement/InterestStatamentPopupContent";
import LateStatementPopupContent from "./late-statement/LateStatementPopupContent";
import { permissionConstants, hasPermission } from "utils/permissionHelper";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import NewWindow from "react-new-window";
import { getErrorMessage } from "utils/errorHandler";
import { MonthlyStatementsModal } from "./monthly-statement/monthly-statements-modal";
import { useCustomFormats } from "hooks/useCustomFormat";
import { useStatements } from "hooks/statements/useStatements";
import { useQueryClient } from "@tanstack/react-query";
import { StatementMenu } from "./components/StatementMenu/StatementMenu";
import { LoanContext } from "providers/loan";
import { RedemptionStatementSearchModal } from "./redemption-statement/RedemptionStatementSearchModal";
import { MortgageStatementSearchModal } from "./mortgage-statement/MortgageStatementSearchModal";

const StatementCreation = () => {
  const { loan } = useContext(LoanContext);
  const { formatDate } = useCustomFormats();

  const { id } = useParams();
  const { showError } = useContext(AlertsContext);
  const { user } = useTenant();

  const {
    isOpen: isMorgageStatementSearchOpen,
    onOpen: openMorgageStatementSearchBox,
    onClose: closeMorgageStatementSearchBox,
  } = useDisclosure();

  const {
    isOpen: isRedemptionStatementSearchOpen,
    onOpen: openRedemptionStatementSearchBox,
    onClose: closeRedemptionStatementSearchBox,
  } = useDisclosure();

  const queryClient = useQueryClient();
  const {
    data: statements,
    isLoading: isStatementsLoading,
    isFetching: isStatementsFetching,
  } = useStatements({
    loanId: id,
  });

  const modalRef = useRef<HTMLDivElement>(null);

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const [monthlyStatementPopup, setMonthlyStatementPopup] = useState(false);
  const [interestStatementPopup, setInterestStatementPopup] = useState(false);
  const [latetStatementPopup, setLatetStatementPopup] = useState(false);
  const [months, setMonths] = useState<SelectOption | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string>("");
  const [singleStatement, setSingleStatement] = useState("");
  const [readLoan, setReadLoan] = useState(false);
  const [loading, setLoading] = useState(false);

  const openSearchBox = () => {
    setIsSearchModalOpen(true);
  };
  const closeSearchBox = () => {
    setIsSearchModalOpen(false);
  };

  const openMonthlyStatementPopup = () => {
    setMonthlyStatementPopup(true);
  };
  const closeMonthlyStatementPopup = () => {
    setMonthlyStatementPopup(false);
  };
  const openInterestStatement = () => {
    setInterestStatementPopup(true);
  };
  const closeInterestStatement = () => {
    setInterestStatementPopup(false);
  };
  const openLatetStatementPopup = () => {
    setLatetStatementPopup(true);
  };
  const closeLatetStatementPopup = () => {
    setLatetStatementPopup(false);
  };

  const readOnlyLoan = useCallback(() => {
    if (
      hasPermission(user, permissionConstants.readOnlyLoans) &&
      !hasPermission(user, permissionConstants.loanAccountStatement) &&
      !hasPermission(user, permissionConstants.viewPayments)
    ) {
      setReadLoan(true);
    }
    if (hasPermission(user, permissionConstants.loanAccountStatement)) {
      setReadLoan(false);
    }
    if (hasPermission(user, permissionConstants.viewPayments)) {
      setReadLoan(false);
    }
  }, [user]);

  useEffect(() => {
    readOnlyLoan();
  }, [readOnlyLoan]);

  const handleMenuItemClick = (itemName: string) => {
    const handleAsyncAction = async () => {
      try {
        setLoading(true);
        const statement = await apiRequest("/supabase/statements/insert", {
          name: itemName,
          loan: id,
          type: "PDF",
        });

        const statementCurrentWithId = statement.statement;
        const currentStatementId = statementCurrentWithId.id;

        const currentStatement = await apiRequest("/supabase/statements/single", {
          currentStatementId,
        });
        setSingleStatement(currentStatement);

        queryClient.invalidateQueries({
          queryKey: ["statements", id],
        });
      } catch (err) {
        showError({
          title: "Error Creating Loan",
          description: getErrorMessage(err),
        });
      } finally {
        setLoading(false);
      }
    };
    handleAsyncAction();
  };

  const handleViewStatementClick = async () => {
    setLoading(true);
    if (months) {
      openMonthlyStatementPopup();
      setMonths(null);
    } else {
      if (months) {
        showError({
          title: "Please select a Month to generate statement",
          description: "You need to select months to generate statement",
        });
      } else {
        showError({
          title: "Please select a Date to generate statement",
          description: "You need to select date to generate statement",
        });
      }
    }
    setLoading(false);
  };

  const handleViewStatement = async (id: any, pdfUrl: any) => {
    if (pdfUrl !== null) {
      const response = await apiRequest("/supabase/statements/download", {
        id,
      });
      setDownloadUrl(response?.signedUrl);
    } else {
      showError({
        title: "Statement not found",
        description: "We were unable to find this statement",
      });
    }
  };

  function fundingDateIsNotEmpty() {
    const value =
      loan?.funding_date ||
      loan?.values.find(v => v.loan_field.data_key === "funding_date")?.value?.field;

    return value !== null;
  }

  function projectDurationIsNotEmpty() {
    const value =
      loan?.duration || loan?.values.find(v => v.loan_field.data_key === "duration")?.value?.field;

    return value > 0;
  }

  function isDisabled() {
    return !fundingDateIsNotEmpty() || !projectDurationIsNotEmpty();
  }

  function getDisabledReason(): string {
    if (!fundingDateIsNotEmpty() || !projectDurationIsNotEmpty()) {
      return "Please, set the funding date and project duration on overview page to create a statement";
    }

    return "";
  }

  return (
    <Flex flexDirection="column" gap={4}>
      {user.type === "lender" && (
        <Flex gap={2} justifyContent="flex-end">
          {downloadUrl && <NewWindow url={downloadUrl} onUnload={() => setDownloadUrl("")} />}
          {isDisabled() ? (
            <Tooltip label={getDisabledReason()}>
              <span>
                <Button isDisabled size="sm" rightIcon={<ChevronDownIcon />} variant="white">
                  Create Statement
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                variant="white"
                size="sm"
                isDisabled={readLoan || isDisabled()}
              >
                Create Statement
              </MenuButton>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    openMorgageStatementSearchBox();
                  }}
                >
                  Mortgage Statement
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    openRedemptionStatementSearchBox();
                  }}
                >
                  Redemption Statement
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    openSearchBox();
                  }}
                >
                  Monthly Statement
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    openInterestStatement();
                    handleMenuItemClick("Interest Statement");
                  }}
                >
                  Interest Statement
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    openLatetStatementPopup();
                    handleMenuItemClick("Late Statement");
                  }}
                >
                  Late Statement
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
      )}

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th> Date Created</Th>
              <Th width="100px"></Th>
              {user.type === "lender" && <Th width="36px"></Th>}
            </Tr>
          </Thead>
          <Tbody>
            {isStatementsLoading || isStatementsFetching ? (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  <Spinner />
                </Td>
              </Tr>
            ) : statements && statements.length > 0 ? (
              statements
                .filter(statement => statement.pdf_statement !== null)
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map(statement => {
                  return (
                    <Tr key={statement.id}>
                      <Td>{statement.name}</Td>
                      <Td>
                        <Badge variant="purple">{statement.type}</Badge>
                      </Td>
                      <Td>{formatDate(statement.created_at)}</Td>
                      <Td>
                        <Button
                          variant="outline"
                          onClick={() =>
                            handleViewStatement(statement?.id, statement?.pdf_statement)
                          }
                          size="xs"
                        >
                          View Statement
                        </Button>
                      </Td>
                      {user.type === "lender" && (
                        <Td>
                          <Flex justifyContent="center">
                            <StatementMenu statement={statement} />
                          </Flex>
                        </Td>
                      )}
                    </Tr>
                  );
                })
            ) : (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Text p="1rem">You haven't added any statements yet.</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {isSearchModalOpen && (
        <Modal isOpen={isSearchModalOpen} onClose={closeSearchBox}>
          <ModalOverlay />
          <ModalContent minW="555px">
            <ModalCloseButton />
            <ModalHeader>Statements</ModalHeader>
            <Text marginLeft={"24px"}>View and download statements </Text>
            <ModalHeader>Monthly Statements</ModalHeader>
            <Box width={"60%"} marginLeft={"24px"}>
              <MonthSelect months={months} setMonths={setMonths} />
            </Box>
            <ModalHeader>Closing Statements</ModalHeader>
            <Text marginLeft={"24px"}> Buyer borrower closing statement </Text>

            {/* height added for the text only */}
            <Text height={"250px"}></Text>
            <Box>
              <Button
                onClick={closeSearchBox}
                marginLeft={"20px"}
                marginBottom={"10px"}
                background={"none"}
                color={"black"}
              >
                Close
              </Button>
              <Button
                marginLeft={"53%"}
                marginBottom={"10px"}
                variant={getCustomButtonVariant(user)}
                onClick={handleViewStatementClick}
                isLoading={loading}
              >
                View Statement
              </Button>
            </Box>
          </ModalContent>
        </Modal>
      )}

      {monthlyStatementPopup && (
        <MonthlyStatementsModal
          isOpen={monthlyStatementPopup}
          onClose={closeMonthlyStatementPopup}
          months={months?.value ?? ""}
        />
      )}

      {interestStatementPopup && (
        <Modal isOpen={interestStatementPopup} onClose={closeInterestStatement}>
          <ModalOverlay />
          <InterestStatamentPopupContent
            printref={modalRef}
            singleStatement={singleStatement}
            onClose={closeInterestStatement}
          />
        </Modal>
      )}

      {latetStatementPopup && (
        <Modal isOpen={latetStatementPopup} onClose={closeLatetStatementPopup}>
          <ModalOverlay />
          <LateStatementPopupContent
            printref={modalRef}
            singleStatement={singleStatement}
            onClose={closeLatetStatementPopup}
          />
        </Modal>
      )}

      {id && isMorgageStatementSearchOpen && (
        <MortgageStatementSearchModal
          isOpen={isMorgageStatementSearchOpen}
          onClose={closeMorgageStatementSearchBox}
        />
      )}

      {id && isRedemptionStatementSearchOpen && (
        <RedemptionStatementSearchModal
          isOpen={isRedemptionStatementSearchOpen}
          onClose={closeRedemptionStatementSearchBox}
        />
      )}
    </Flex>
  );
};

export default StatementCreation;
